import { ChessTermType, TermAction, } from "@chesshotel/types";
import { detectPositiveFollowsCheckmate, detectNegativeFollowsCheckmate, } from "./detect/ending.js";
import { detectNegativeThreat, detectPositiveThreat, } from "./detect/attacks.js";
import { detectNegativeDefence, detectPositiveDefence, } from "./detect/defence.js";
import { detectNegativeDevelop, detectPerformDevelopPiece, } from "./detect/opening.js";
import { negativePawnPlacements, positivePawnPlacements, } from "./detect/pawnPlacement.js";
import { detectNegativeTactics, detectPositiveTactics, detectPositiveTacticThreats, } from "./detect/tactics/index.js";
import { detectNegativeOfferTrade, detectNegativeTrade, detectPositiveTrades, } from "./detect/offerTrade.js";
import { detectNegativeMaterialWin, detectPositiveMaterialWin, } from "./detect/materialWin.js";
import { detectAllThreats, detectSEECombinations, } from "./detect/combinations.js";
import { detectNegativeStockfishMaterialWin, detectPositiveStockfishMaterialWin, } from "./detect/stockfishMaterialWin.js";
import { detectNegativePlacement, detectPositivePlacement, } from "./detect/placement.js";
import { negativeKingSafety, negativePreventCastling, positiveKingSafety, positivePreventCastling, } from "./detect/kingSafety.js";
import { detectMateThreatNegative, detectMateThreatPositive, } from "./detect/mateThreat.js";
import { detectDefendMateNegative, detectDefendMatePositive, } from "./detect/defendMate.js";
import { detectPositiveSacrifice, } from "./detect/sacrifice.js";
import { detectNegativeGameInitiative, detectPositiveGameInitiative, } from "./detect/gameInitiative.js";
import { isNegativeMove, isTactic, isThreat, uci2move, } from "./detect/util.js";
import { Chess } from "@chesshotel/chess";
import { detectGreatMove, detectBrilliantMove, } from "./detect/greatBrilliantMove.js";
import { chessTermScore, termActionPriority } from "./chessTermScore.js";
import { detectNegativeCheck, detectPositiveCheck } from "./detect/check.js";
import { detectNegativeOpenFile, detectPositiveOpenFile, } from "./detect/openFile.js";
const ENABLE_GAME_REVIEW_TIMING = false;
function logTiming(phase, moveIndex, move, label, duration) {
    if (!ENABLE_GAME_REVIEW_TIMING)
        return;
    try {
        console.log(`[GameReviewTiming] phase=${phase} moveIndex=${moveIndex} move=${move || ""} label=${label} duration=${duration.toFixed(2)}ms`);
    }
    catch {
    }
}
function addArrows(lastPosition, position) {
    const { chessTerms } = position;
    const userBestMoveObj = uci2move(lastPosition.bestMove);
    const opponentBestMoveObj = uci2move(position.bestMove);
    chessTerms.forEach((chessTerm) => {
        if (chessTerm.termAction === TermAction.MISS && !chessTerm.arrows) {
            chessTerm.arrows = [userBestMoveObj];
        }
        if (chessTerm.termAction === TermAction.BETTER_WAY && !chessTerm.arrows) {
            chessTerm.arrows = [userBestMoveObj];
        }
        if (chessTerm.termAction === TermAction.ALLOW && !chessTerm.arrows) {
            chessTerm.arrows = [opponentBestMoveObj];
        }
    });
}
function shouldShowPVTerm(position) {
    const showPVTerm = position.chessTerms.find(({ term, termAction, pvMoves }) => (term === ChessTermType.TACTIC ||
        term === ChessTermType.FORK ||
        term === ChessTermType.DOUBLE_ATTACK ||
        term === ChessTermType.REMOVE_DEFENDER ||
        term === ChessTermType.DISCOVERED_ATTACK ||
        term === ChessTermType.FOLLOW_CHECKMATE) &&
        termAction !== TermAction.PERFORM_THREAT &&
        pvMoves !== null);
    if (showPVTerm) {
        position.showPVTerm = showPVTerm;
    }
}
function detectGameEnding(fen) {
    const chess = new Chess(fen);
    let chessTerms = null;
    if (chess.isCheckmate()) {
        chessTerms = [
            { term: ChessTermType.CHECKMATE, termAction: TermAction.PERFORM },
        ];
    }
    else if (chess.isDraw()) {
        if (chess.isStalemate()) {
            chessTerms = [
                { term: ChessTermType.STALE_MATE, termAction: TermAction.PERFORM },
            ];
        }
        else {
            chessTerms = [
                { term: ChessTermType.DRAW, termAction: TermAction.PERFORM },
            ];
        }
    }
    return chessTerms;
}
export async function generateCommentary(gameReview, elo, onProgress) {
    const allMoves = [];
    gameReview.forEach((position) => {
        if (position?.move) {
            allMoves.push(uci2move(position.move));
        }
    });
    gameReview.forEach((position) => {
        position.chessTerms = null;
    });
    detectAllThreats(gameReview);
    detectSEECombinations(gameReview);
    const start = performance.now();
    onProgress?.(80);
    for (let i = 1; i < gameReview.length; i++) {
        const moveStart = performance.now();
        const lastPosition = gameReview[i - 1];
        const position = gameReview[i];
        let chessTerms = null;
        let commentaryType = null;
        const gameEnding = i === gameReview.length - 1 && detectGameEnding(position.fen);
        if (!gameEnding) {
            if (!position?.pvMoves ||
                position?.pvMoves?.length == 0 ||
                lastPosition?.pvMoves?.length == 0) {
                continue;
            }
        }
        if (gameEnding) {
            position.chessTerms = gameEnding;
            continue;
        }
        const negativeMove = isNegativeMove(position);
        if (negativeMove) {
            commentaryType = "negative";
            chessTerms = await generateNegativeChessTerms(position, lastPosition, allMoves, i);
        }
        else {
            commentaryType = "positive";
            chessTerms = await generatePositiveChessTerms(position, lastPosition, allMoves, i);
        }
        position.chessTerms = filterChessTerms(chessTerms, negativeMove, position);
        position.unfilteredChessTerms = chessTerms;
        addArrows(lastPosition, position);
        if (!negativeMove) {
            detectGreatMove(position, lastPosition);
            detectBrilliantMove(position, lastPosition);
        }
        shouldShowPVTerm(position);
        logTiming("move", i, position.move, "generateCommentaryMove", performance.now() - moveStart);
        const total = Math.max(1, gameReview.length - 1);
        const pct = 80 + Math.round((i / total) * 20);
        onProgress?.(Math.min(100, pct));
        await new Promise((resolve) => setTimeout(resolve, 0));
    }
    return gameReview;
}
async function generateNegativeChessTerms(position, lastPosition, allMoves, moveIndex) {
    const fnStart = performance.now();
    let chessTerms = position.chessTerms || [];
    let t0 = performance.now();
    const followCheckmate = await detectNegativeFollowsCheckmate(position, lastPosition);
    logTiming("negative", moveIndex, position.move, "detectNegativeFollowsCheckmate", performance.now() - t0);
    followCheckmate && chessTerms.push(followCheckmate);
    t0 = performance.now();
    const defendMateNeg = await detectDefendMateNegative(position, lastPosition);
    logTiming("negative", moveIndex, position.move, "detectDefendMateNegative", performance.now() - t0);
    defendMateNeg && chessTerms.push(defendMateNeg);
    t0 = performance.now();
    const negativeMaterialWin = detectNegativeMaterialWin(position, lastPosition);
    logTiming("negative", moveIndex, position.move, "detectNegativeMaterialWin", performance.now() - t0);
    const sortedNegativeMaterialWin = negativeMaterialWin
        ? sortByTermActionPriority(negativeMaterialWin)[0].chessTerm
        : null;
    if (sortedNegativeMaterialWin) {
        chessTerms = chessTerms.concat(negativeMaterialWin);
    }
    t0 = performance.now();
    const negativeOfferTrade = detectNegativeOfferTrade(position, lastPosition);
    logTiming("negative", moveIndex, position.move, "detectNegativeOfferTrade", performance.now() - t0);
    negativeOfferTrade && chessTerms.push(negativeOfferTrade);
    t0 = performance.now();
    const negativeTrade = detectNegativeTrade(position, lastPosition);
    logTiming("negative", moveIndex, position.move, "detectNegativeTrade", performance.now() - t0);
    negativeTrade && chessTerms.push(negativeTrade);
    t0 = performance.now();
    const negativeThreat = detectNegativeThreat(position, lastPosition);
    logTiming("negative", moveIndex, position.move, "detectNegativeThreat", performance.now() - t0);
    negativeThreat && chessTerms.push(negativeThreat);
    t0 = performance.now();
    const negativeDefence = detectNegativeDefence(position, lastPosition);
    logTiming("negative", moveIndex, position.move, "detectNegativeDefence", performance.now() - t0);
    chessTerms = chessTerms.concat(negativeDefence);
    t0 = performance.now();
    const kingSafety = negativeKingSafety(position, lastPosition);
    logTiming("negative", moveIndex, position.move, "negativeKingSafety", performance.now() - t0);
    kingSafety && chessTerms.push(kingSafety);
    t0 = performance.now();
    const preventCastling = negativePreventCastling(position, lastPosition);
    logTiming("negative", moveIndex, position.move, "negativePreventCastling", performance.now() - t0);
    preventCastling && chessTerms.push(preventCastling);
    t0 = performance.now();
    const negativeDevelop = detectNegativeDevelop(position, lastPosition, allMoves);
    logTiming("negative", moveIndex, position.move, "detectNegativeDevelop", performance.now() - t0);
    negativeDevelop && chessTerms.push(negativeDevelop);
    t0 = performance.now();
    const negativePawns = negativePawnPlacements(position, lastPosition);
    logTiming("negative", moveIndex, position.move, "negativePawnPlacements", performance.now() - t0);
    chessTerms = chessTerms.concat(negativePawns);
    t0 = performance.now();
    const negativePlacement = detectNegativePlacement(position, lastPosition);
    logTiming("negative", moveIndex, position.move, "detectNegativePlacement", performance.now() - t0);
    negativePlacement && chessTerms.push(negativePlacement);
    t0 = performance.now();
    const negativeOpenFile = detectNegativeOpenFile(position, lastPosition);
    logTiming("negative", moveIndex, position.move, "detectNegativeOpenFile", performance.now() - t0);
    negativeOpenFile && chessTerms.push(negativeOpenFile);
    t0 = performance.now();
    const mateThreat = detectMateThreatNegative(position, lastPosition);
    logTiming("negative", moveIndex, position.move, "detectMateThreatNegative", performance.now() - t0);
    mateThreat && chessTerms.push(mateThreat);
    t0 = performance.now();
    const negativeTactics = detectNegativeTactics(position, lastPosition);
    logTiming("negative", moveIndex, position.move, "detectNegativeTactics", performance.now() - t0);
    negativeTactics && chessTerms.push(negativeTactics);
    const shouldCheckStockfishMaterialWin = (!negativeTactics || negativeTactics.term == ChessTermType.UNPIN) &&
        !sortedNegativeMaterialWin;
    if (shouldCheckStockfishMaterialWin) {
        t0 = performance.now();
        const stockfishMaterialWin = detectNegativeStockfishMaterialWin(position, lastPosition, sortedNegativeMaterialWin);
        logTiming("negative", moveIndex, position.move, "detectNegativeStockfishMaterialWin", performance.now() - t0);
        stockfishMaterialWin && chessTerms.push(stockfishMaterialWin);
    }
    t0 = performance.now();
    const gameInitiative = detectNegativeGameInitiative(position, lastPosition);
    logTiming("negative", moveIndex, position.move, "detectNegativeGameInitiative", performance.now() - t0);
    gameInitiative && chessTerms.push(gameInitiative);
    t0 = performance.now();
    const check = detectNegativeCheck(position, lastPosition);
    logTiming("negative", moveIndex, position.move, "detectNegativeCheck", performance.now() - t0);
    if (check)
        chessTerms.push(check);
    if (chessTerms.length == 0) {
        chessTerms.push({ term: ChessTermType.NONE });
    }
    logTiming("negative", moveIndex, position.move, "generateNegativeChessTerms_total", performance.now() - fnStart);
    return chessTerms;
}
async function generatePositiveChessTerms(position, lastPosition, allMoves, moveIndex) {
    const fnStart = performance.now();
    let chessTerms = position.chessTerms || [];
    let t0 = performance.now();
    const followCheckmate = detectPositiveFollowsCheckmate(position, lastPosition);
    logTiming("positive", moveIndex, position.move, "detectPositiveFollowsCheckmate", performance.now() - t0);
    followCheckmate && chessTerms.push(followCheckmate);
    t0 = performance.now();
    const defendMatePos = detectDefendMatePositive(position, lastPosition);
    logTiming("positive", moveIndex, position.move, "detectDefendMatePositive", performance.now() - t0);
    defendMatePos && chessTerms.push(defendMatePos);
    t0 = performance.now();
    const positiveTactics = detectPositiveTactics(position, lastPosition);
    logTiming("positive", moveIndex, position.move, "detectPositiveTactics", performance.now() - t0);
    positiveTactics && chessTerms.push(positiveTactics);
    if (!positiveTactics) {
        t0 = performance.now();
        const positiveTacticThreats = detectPositiveTacticThreats(position, lastPosition);
        logTiming("positive", moveIndex, position.move, "detectPositiveTacticThreats", performance.now() - t0);
        positiveTacticThreats && chessTerms.push(positiveTacticThreats);
    }
    t0 = performance.now();
    const positiveMaterialWin = detectPositiveMaterialWin(position, lastPosition);
    logTiming("positive", moveIndex, position.move, "detectPositiveMaterialWin", performance.now() - t0);
    positiveMaterialWin && chessTerms.push(positiveMaterialWin);
    t0 = performance.now();
    const positiveThreat = detectPositiveThreat(position, lastPosition);
    logTiming("positive", moveIndex, position.move, "detectPositiveThreat", performance.now() - t0);
    positiveThreat && chessTerms.push(positiveThreat);
    t0 = performance.now();
    const positiveTrade = !positiveThreat && detectPositiveTrades(position, lastPosition);
    logTiming("positive", moveIndex, position.move, "detectPositiveTrades", performance.now() - t0);
    positiveTrade && chessTerms.push(positiveTrade);
    const shouldCheckStockfishMaterialWin = !(positiveTactics && positiveTactics.term != ChessTermType.UNPIN) &&
        !positiveMaterialWin &&
        !position.chessTerm;
    if (shouldCheckStockfishMaterialWin) {
        t0 = performance.now();
        const stockfishMaterialWin = detectPositiveStockfishMaterialWin(position, lastPosition);
        logTiming("positive", moveIndex, position.move, "detectPositiveStockfishMaterialWin", performance.now() - t0);
        stockfishMaterialWin && chessTerms.push(stockfishMaterialWin);
    }
    t0 = performance.now();
    const positiveDefence = detectPositiveDefence(position, lastPosition);
    logTiming("positive", moveIndex, position.move, "detectPositiveDefence", performance.now() - t0);
    positiveDefence && chessTerms.push(positiveDefence);
    t0 = performance.now();
    const kingSafety = positiveKingSafety(position, lastPosition);
    logTiming("positive", moveIndex, position.move, "positiveKingSafety", performance.now() - t0);
    kingSafety && chessTerms.push(kingSafety);
    t0 = performance.now();
    const preventCastling = positivePreventCastling(position, lastPosition);
    logTiming("positive", moveIndex, position.move, "positivePreventCastling", performance.now() - t0);
    preventCastling && chessTerms.push(preventCastling);
    t0 = performance.now();
    const performDevelopPiece = detectPerformDevelopPiece(position, lastPosition, allMoves);
    logTiming("positive", moveIndex, position.move, "detectPerformDevelopPiece", performance.now() - t0);
    performDevelopPiece && chessTerms.push(performDevelopPiece);
    t0 = performance.now();
    const positivePawn = positivePawnPlacements(position, lastPosition);
    logTiming("positive", moveIndex, position.move, "positivePawnPlacements", performance.now() - t0);
    chessTerms = chessTerms.concat(positivePawn);
    t0 = performance.now();
    const positivePlacement = detectPositivePlacement(position, lastPosition);
    logTiming("positive", moveIndex, position.move, "detectPositivePlacement", performance.now() - t0);
    positivePlacement && chessTerms.push(positivePlacement);
    t0 = performance.now();
    const positiveOpenFile = detectPositiveOpenFile(position, lastPosition);
    logTiming("positive", moveIndex, position.move, "detectPositiveOpenFile", performance.now() - t0);
    positiveOpenFile && chessTerms.push(positiveOpenFile);
    t0 = performance.now();
    const mateThreat = detectMateThreatPositive(position, lastPosition);
    logTiming("positive", moveIndex, position.move, "detectMateThreatPositive", performance.now() - t0);
    mateThreat && chessTerms.push(mateThreat);
    t0 = performance.now();
    const sacrifice = detectPositiveSacrifice(position, lastPosition);
    logTiming("positive", moveIndex, position.move, "detectPositiveSacrifice", performance.now() - t0);
    sacrifice && chessTerms.push(sacrifice);
    t0 = performance.now();
    const gameInitiative = detectPositiveGameInitiative(position, lastPosition);
    logTiming("positive", moveIndex, position.move, "detectPositiveGameInitiative", performance.now() - t0);
    gameInitiative && chessTerms.push(gameInitiative);
    t0 = performance.now();
    const check = detectPositiveCheck(position, lastPosition);
    logTiming("positive", moveIndex, position.move, "detectPositiveCheck", performance.now() - t0);
    if (check)
        chessTerms.push(check);
    if (chessTerms.length == 0) {
        chessTerms.push({ term: ChessTermType.NONE });
    }
    logTiming("positive", moveIndex, position.move, "generatePositiveChessTerms_total", performance.now() - fnStart);
    return chessTerms;
}
function sortByTermActionPriority(chessTerms) {
    const chessTermsWithScore = chessTerms.map((chessTerm) => {
        const term = chessTerm.term;
        const actionPriority = termActionPriority[chessTerm.termAction];
        const gain = chessTerm.maxGain || chessTerm.evalResult?.gain || 0;
        const gainScore = Math.floor(0.3 * gain);
        let score = chessTermScore[term] + gainScore;
        if (chessTerm.termAction == TermAction.PERFORM_THREAT) {
            score = score - 1;
        }
        return { chessTerm, score, actionPriority };
    });
    let sortedChessTerms = chessTermsWithScore.sort((a, b) => b.score - a.score || b.actionPriority - a.actionPriority);
    return sortedChessTerms;
}
function filterChessTerms(chessTerms, isNegativeMove, position) {
    if (!chessTerms || chessTerms.length === 0)
        return [];
    let sortedChessTerms = sortByTermActionPriority(chessTerms);
    position.log = sortedChessTerms;
    const hasTactic = sortedChessTerms.some(({ chessTerm }) => isTactic(chessTerm.term));
    const hasThreat = sortedChessTerms.some(({ chessTerm }) => isThreat(chessTerm.term));
    if (hasTactic && hasThreat) {
        const lowerScoreTerm = sortedChessTerms[1].chessTerm.term;
        sortedChessTerms = sortedChessTerms.filter(({ chessTerm }) => chessTerm.term !== lowerScoreTerm);
    }
    const filteredChessTerms = sortedChessTerms
        .filter(({ score }, index) => {
        if (index === 0)
            return true;
        return false;
    })
        .map(({ chessTerm }) => chessTerm);
    return filteredChessTerms;
}
//# sourceMappingURL=commentary.js.map